import { useCallback } from "react";
import type { InboxThread } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "../../mappings";
import type { TellescopeQuery } from "../../types";
import { TellescopeMutationInlineOptions } from "../types";
import { useTellescopeMutation } from "../use-tellescope-mutation";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { useCachedTellescopeQueries } from "../use-cached-tellescope-queries";
import { useTellescopeQueryClient } from "../use-tellescope-query-client";
import type { ThreadMessage } from "./use-inbox-conversation";

type MinimumMetadata = Pick<
  InboxThread,
  "type" | "phoneNumber" | "enduserPhoneNumber" | "threadId" | "title" | "enduserIds"
>;

export interface UseUpdateThreadInboxStatusParams<T extends MinimumMetadata> {
  threadId: string;
  metadata?: T;
  messages?: ThreadMessage[];
}

export interface UseUpdateThreadInboxStatusResult {
  updateInboxStatus: (
    { inboxStatus }: { inboxStatus: string },
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads"> | void>;
  isLoading: boolean;
}

export const useUpdateThreadInboxStatus = <T extends MinimumMetadata>({
  threadId,
  metadata: providedMetadata,
  messages: providedMessages,
}: UseUpdateThreadInboxStatusParams<T>): UseUpdateThreadInboxStatusResult => {
  const queryClient = useTellescopeQueryClient();

  // Fetch metadata if not provided
  const cachedMetadataSelect = useCallback(
    (queries: TellescopeQuery<"inbox_threads", "load_threads">[]) => {
      return queries.reduce<InboxThread | undefined>((acc, curr) => {
        if (acc) return acc;

        const match = curr.state.data?.pages.reduce<InboxThread | undefined>((acc, curr) => {
          if (acc) return acc;

          const match = curr.threads.find((thread) => thread.id === threadId);
          return match;
        }, undefined);
        return match;
      }, undefined);
    },
    [threadId]
  );

  const { data: cachedMetadata } = useCachedTellescopeQueries({
    model: "inbox_threads",
    method: "load_threads",
    select: cachedMetadataSelect,
  });

  const fetchedMetadata = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      mdbFilter: {
        id: threadId,
      },
    },
    enabled: !providedMetadata && !cachedMetadata,
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.at(0)?.threads.at(0),
  });

  const metadata = providedMetadata ?? cachedMetadata ?? fetchedMetadata.data;
  const updateThreadMutation = useTellescopeMutation({
    model: "inbox_threads",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const updateSMSMessageMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const updateChatRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const updateEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const updateUnderlyingMessage = useCallback(
    async (inboxStatus: string) => {
      // Use provided messages if available, otherwise fetch on-demand
      let latestMessageId: string | undefined;

      if (providedMessages) {
        // Use the latest message from provided messages
        latestMessageId = providedMessages.at(-1)?.id;
      }

      switch (metadata?.type) {
        case "SMS": {
          // Fetch latest SMS message only if not provided
          if (!latestMessageId && metadata.phoneNumber && metadata.enduserPhoneNumber) {
            const data = await queryClient.fetchInfiniteQuery({
              model: "sms_messages",
              method: "getSome",
              request: {
                filter: {
                  phoneNumber: metadata.phoneNumber,
                  enduserPhoneNumber: metadata.enduserPhoneNumber,
                },
                limit: 1,
              },
              getNextPageParam: (lastPage) => lastPage.at(-1)?.id,
              initialPageParam: undefined,
              paginateRequest: (lastId, request) => ({
                ...request,
                lastId,
              }),
              pages: 1,
            });
            latestMessageId = data.pages[0]?.at(-1)?.id;
          }

          if (!latestMessageId) return;

          await updateSMSMessageMutation.mutateAsync({
            id: latestMessageId,
            inboxStatus,
            _options: {
              replaceObjectFields: false,
            },
          });
          break;
        }

        case "Chat": {
          // Chat messages don't have inboxStatus field
          // Only the thread has inboxStatus
          if (metadata?.threadId) {
            await updateChatRoomMutation.mutateAsync({
              id: metadata.threadId,
              inboxStatus,
              _options: {
                replaceObjectFields: false,
              },
            });
          }
          break;
        }

        case "Email": {
          // Fetch latest email message only if not provided
          if (!latestMessageId && metadata.title && metadata.enduserIds.length > 0) {
            const data = await queryClient.fetchInfiniteQuery({
              model: "emails",
              method: "getSome",
              request: {
                filter: {
                  enduserId: metadata.enduserIds[0],
                },
                limit: 1000,
              },
              getNextPageParam: (lastPage) => lastPage.at(-1)?.id,
              initialPageParam: undefined,
              paginateRequest: (lastId, request) => ({
                ...request,
                lastId,
              }),
              pages: 1,
            });

            // Filter by subject line to match the thread
            const normalizeSubject = (subject: string) => subject.replace(/^(Re:\s*)+/i, "").trim();
            const normalizedTarget = normalizeSubject(metadata.title);
            const allEmails = data.pages.flat();

            // Filter emails with matching normalized subject
            const matchingEmails = allEmails.filter((email) => {
              if (!email.subject) return false;
              return normalizeSubject(email.subject) === normalizedTarget;
            });

            // Sort chronologically by createdAt (earliest first) and get the last one
            const sortedEmails = matchingEmails.sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateA - dateB;
            });

            latestMessageId = sortedEmails.at(-1)?.id;
          }

          if (!latestMessageId) return;

          await updateEmailMutation.mutateAsync({
            id: latestMessageId,
            inboxStatus,
            _options: {
              replaceObjectFields: false,
            },
          });
          break;
        }
      }
    },
    [
      providedMessages,
      metadata?.type,
      metadata?.phoneNumber,
      metadata?.enduserPhoneNumber,
      metadata?.threadId,
      metadata?.title,
      metadata?.enduserIds,
      queryClient,
      updateSMSMessageMutation,
      updateChatRoomMutation,
      updateEmailMutation,
    ]
  );

  const updateInboxStatus = useCallback<UseUpdateThreadInboxStatusResult["updateInboxStatus"]>(
    async ({ inboxStatus }, options) => {
      return updateThreadMutation.mutateAsync(
        {
          id: threadId,
          inboxStatus,
          _options: {
            replaceObjectFields: false,
          },
        },
        {
          ...options,
          onSuccess: async (res, req, ...rest) => {
            options?.onSuccess?.(res, req, ...rest);
            await updateUnderlyingMessage(inboxStatus);
          },
        }
      );
    },
    [threadId, updateThreadMutation, updateUnderlyingMessage]
  );

  return {
    updateInboxStatus,
    isLoading:
      updateThreadMutation.isPending ||
      updateSMSMessageMutation.isPending ||
      updateChatRoomMutation.isPending ||
      updateEmailMutation.isPending ||
      fetchedMetadata.isLoading,
  };
};
